import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PageHeader } from "../components/PageHeader";
import { AnchorNav } from "../components/AnchorNav";
import { ClosingCTA } from "../components/marketing/ClosingCTA";
import { Reveal } from "../components/anim";
import { api, mediaUrl } from "../lib/api";
import { ArrowRight, ExternalLink, Search, X, Loader2, Building2, Briefcase, Code2 } from "lucide-react";

const anchorItems = [
  { label: "Anchor Partners", href: "#anchor" },
  { label: "Who Joins", href: "#who" },
  { label: "Members Directory", href: "#directory" },
];

const userTypes = [
  {
    icon: Briefcase,
    label: "For Investors",
    description: "Get verified once. Access regulated tokenised assets across venues with identity that travels with you.",
    href: "/solutions/institutions",
    testid: "user-type-investors",
  },
  {
    icon: Building2,
    label: "For Issuers",
    description: "Issue once on T-REX Ledger. Distribute everywhere with compliance and ownership state preserved across chains.",
    href: "/solutions/institutions",
    testid: "user-type-issuers",
  },
  {
    icon: Code2,
    label: "For Builders",
    description: "Ship apps, earn fees, and reach institutional asset flows through the T-REX App Store.",
    href: "/solutions/builders",
    testid: "user-type-builders",
  },
];

function MemberCard({ m }) {
  const hasCase = !!m.case_study_slug;
  const caseUrl = hasCase ? `/articles/${m.case_study_slug}` : null;
  const tag = m.member_status;          // "" | "Builder" | "Anchor Partner"
  return (
    <div
      data-testid={`member-card-${m.member_id}`}
      className="group relative rounded-md border border-champ-light/10 bg-aubergine-tier hover:border-champagne/40 transition-colors p-5 flex flex-col h-full"
    >
      <div className="flex items-start gap-4">
        {m.logo_media_id ? (
          <div className="h-12 w-12 rounded bg-bone flex-shrink-0 flex items-center justify-center overflow-hidden p-1.5">
            <img src={mediaUrl(m.logo_media_id)} alt={m.name} className="max-h-full max-w-full object-contain" loading="lazy" />
          </div>
        ) : (
          <div className="h-12 w-12 rounded bg-aubergine-raised flex-shrink-0 flex items-center justify-center font-serif text-lg text-champ-light/70 italic">
            {m.name.slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-champagne">{m.category}</p>
            {tag && (
              <span
                className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em] ${
                  tag === "Anchor Partner" ? "text-champ-light" : "text-bone/55"
                }`}
                data-testid={`member-tag-${m.member_id}`}
              >
                <span className={`inline-block h-1 w-1 rounded-full ${tag === "Anchor Partner" ? "bg-champagne" : "bg-bone/40"}`} />
                {tag === "Anchor Partner" ? "Anchor" : "Builder"}
              </span>
            )}
          </div>
          <h3 className="mt-1 text-base font-medium text-bone truncate">{m.name}</h3>
        </div>
        {m.website_url && (
          <a
            href={m.website_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`${m.name} website`}
            data-testid={`member-website-${m.member_id}`}
            className="text-bone/35 hover:text-champagne transition-colors flex-shrink-0"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
      {m.description && (
        <p className="mt-3 text-sm text-bone/70 leading-relaxed line-clamp-3 flex-1">{m.description}</p>
      )}
      {hasCase && (
        <Link
          to={caseUrl}
          data-testid={`member-case-study-${m.member_id}`}
          className="mt-4 inline-flex items-center font-mono text-[11px] uppercase tracking-[0.15em] text-champagne hover:text-champ-light"
        >
          {m.case_study_title ? `Case study: ${m.case_study_title}` : "Read case study"}
          <ArrowRight className="ml-2 h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  // Build a compact numeric pagination with edge + neighbour pages and
  // ellipses in between (e.g. 1 … 4 5 [6] 7 8 … 13). Keeps the strip
  // scannable when the member list grows past a couple hundred.
  const pages = [];
  const near = 1;
  pages.push(1);
  const start = Math.max(2, page - near);
  const end = Math.min(totalPages - 1, page + near);
  if (start > 2) pages.push("…l");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages - 1) pages.push("…r");
  if (totalPages > 1) pages.push(totalPages);

  const btn =
    "min-w-[2.25rem] h-9 rounded-md font-mono text-[11px] uppercase tracking-[0.12em] px-3 inline-flex items-center justify-center transition-colors";

  return (
    <nav
      role="navigation"
      aria-label="Members directory pagination"
      className="mt-10 flex flex-wrap items-center justify-center gap-2"
      data-testid="ecosystem-pagination"
    >
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(Math.max(1, page - 1))}
        data-testid="ecosystem-page-prev"
        className={`${btn} border border-lava/30 text-lava/80 hover:text-lava hover:border-lava/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-lava/30`}
      >
        Prev
      </button>
      {pages.map((p, idx) =>
        typeof p === "number" ? (
          <button
            key={p}
            type="button"
            aria-current={p === page ? "page" : undefined}
            onClick={() => onChange(p)}
            data-testid={`ecosystem-page-${p}`}
            className={
              p === page
                ? `${btn} bg-lava text-bone border border-lava shadow-sm shadow-lava/30`
                : `${btn} text-lava/70 hover:text-lava hover:bg-lava/10 border border-transparent`
            }
          >
            {p}
          </button>
        ) : (
          <span key={`${p}-${idx}`} className="px-1 text-lava/50 select-none" aria-hidden="true">
            …
          </span>
        )
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        data-testid="ecosystem-page-next"
        className={`${btn} border border-lava/30 text-lava/80 hover:text-lava hover:border-lava/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-lava/30`}
      >
        Next
      </button>
    </nav>
  );
}


export default function Ecosystem() {
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12; // 4 rows × 3 cols on desktop

  useEffect(() => {
    setLoading(true);
    api.get("/members").then((r) => {
      setMembers(r.data.members);
      setCategories(r.data.categories);
    }).finally(() => setLoading(false));
  }, []);

  // Reset to first page whenever filters change so users never land on an
  // empty page.
  useEffect(() => { setPage(1); }, [activeCat, q]);

  const anchors = useMemo(() => members.filter((m) => m.featured), [members]);
  const directory = useMemo(() => {
    const term = q.trim().toLowerCase();
    return members.filter((m) => {
      if (activeCat && m.category !== activeCat) return false;
      if (term) {
        const hay = `${m.name} ${m.description || ""} ${m.category}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [members, activeCat, q]);

  const totalPages = Math.max(1, Math.ceil(directory.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedDirectory = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return directory.slice(start, start + PAGE_SIZE);
  }, [directory, currentPage]);

  const countsByCat = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.category] = (map[m.category] || 0) + 1; });
    return map;
  }, [members]);

  return (
    <main className="min-h-screen bg-aubergine" data-testid="ecosystem-page">
      <Helmet>
        <title>Ecosystem · T-REX Network</title>
        <meta name="description" content="The ERC-3643 ecosystem. Anchor partners, builders, issuers, service providers, infrastructure and investors. Discover the institutional partners building on T-REX Network." />
        <meta property="og:title" content="Ecosystem · T-REX Network" />
        <meta property="og:description" content="The ERC-3643 ecosystem directory." />
      </Helmet>
      <Navbar />
      <PageHeader
        eyebrow="Ecosystem"
        headline="The institutional ecosystem for tokenised assets."
        highlightWord="institutional"
        subheadline="Anchor partners, builders, issuers, service providers and infrastructure building the T-REX Network."
        primaryCta={{ label: "Become a member", href: "/contact?inquiry=partner" }}
        secondaryCta={{ label: "Read the docs", href: "https://docs.t-rex.network" }}
        compact
      />
      <AnchorNav items={anchorItems} />

      {/* ---------------------------- Anchor partners ---------------------------- */}
      <section id="anchor" className="surface-day py-20 lg:py-28" data-testid="ecosystem-anchor-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div className="max-w-2xl">
                <p className="font-mono text-[11px] font-medium text-champagne uppercase tracking-[0.2em] mb-3">Anchor Partners</p>
                <h2 className="trex-h2 text-bone leading-[1.1]">
                  Institutions <em className="accent-italic">shaping</em> T-REX Network.
                </h2>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-bone/45">
                {anchors.length} anchor{anchors.length === 1 ? "" : "s"}
              </p>
            </div>
          </Reveal>

          {loading ? (
            <div className="py-12 flex items-center justify-center text-bone/55"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : anchors.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-champ-light/15 p-10 text-center text-bone/55" data-testid="ecosystem-anchor-empty">
              <p className="text-sm">Anchor partners will appear here.</p>
              <p className="mt-1 text-xs text-bone/40">Mark a member as &quot;featured&quot; in the admin to surface it as an anchor partner.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {anchors.map((m, i) => (
                <Reveal key={m.member_id} delay={i * 70}>
                  <MemberCard m={m} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ----------------------------- Who joins ----------------------------- */}
      <section id="who" className="surface-night py-20 lg:py-28" data-testid="ecosystem-who-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <p className="font-mono text-[11px] font-medium text-champagne uppercase tracking-[0.2em] mb-3">Who joins</p>
              <h2 className="trex-h2 text-bone leading-[1.1]">
                Three audiences. One <em className="accent-italic">canonical</em> ledger.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {userTypes.map((u, i) => (
              <Reveal key={u.label} delay={i * 90}>
                <Link
                  to={u.href}
                  data-testid={u.testid}
                  className="group rounded-md border border-champ-light/10 bg-aubergine-tier p-8 hover:border-champagne/50 transition-colors flex flex-col h-full"
                >
                  <u.icon className="h-5 w-5 text-champagne" />
                  <p className="mt-5 font-mono text-[10px] font-medium tracking-[0.08em] uppercase text-bone/55">{u.label}</p>
                  <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{u.description}</p>
                  <div className="mt-6 inline-flex items-center text-champagne group-hover:text-champ-light font-mono text-[11px] uppercase tracking-[0.15em] transition-colors">
                    Explore
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------- Directory ----------------------------- */}
      <section id="directory" className="surface-day py-20 lg:py-28" data-testid="ecosystem-directory-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
              <div className="max-w-2xl">
                <p className="font-mono text-[11px] font-medium text-champagne uppercase tracking-[0.2em] mb-3">Members</p>
                <h2 className="trex-h2 text-bone leading-[1.1]">
                  ERC-3643 <em className="accent-italic">members</em> directory.
                </h2>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-bone/45" data-testid="ecosystem-directory-count">
                {directory.length === 0
                  ? `0 of ${members.length} shown`
                  : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, directory.length)} of ${directory.length}${activeCat || q ? "" : ` (of ${members.length})`}`}
              </p>
            </div>
          </Reveal>

          {/* Search + filters */}
          <div className="flex flex-col gap-5 mb-10">
            {/* Search — full width, prominent. Stays the same shade on focus,
                only the border changes (no dark flash). */}
            <div className="relative" data-testid="ecosystem-search-wrap">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-bone/45" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search members by name, description or category…"
                data-testid="ecosystem-search"
                aria-label="Search members"
                className="w-full rounded-md border border-champ-light/15 bg-aubergine-tier pl-11 pr-10 py-3 text-base text-bone placeholder:text-bone/40 outline-none focus:border-champagne transition-colors"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  data-testid="ecosystem-search-clear"
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-bone/45 hover:text-bone hover:bg-aubergine/40"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="ecosystem-filter-bar">
              <button
                type="button"
                onClick={() => setActiveCat(null)}
                data-testid="ecosystem-filter-all"
                className={`font-mono text-[11px] uppercase tracking-[0.08em] rounded-md border px-3 py-1.5 transition-all ${
                  activeCat === null
                    ? "border-champ-light bg-champ-light text-aubergine"
                    : "border-champ-light/15 text-bone/65 hover:text-bone hover:border-champ-light/30"
                }`}
              >
                All <span className={`ml-1 ${activeCat === null ? "text-aubergine/60" : "text-bone/45"}`}>{members.length}</span>
              </button>
              {categories.map((c) => {
                const isActive = activeCat === c;
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setActiveCat(c)}
                    data-testid={`ecosystem-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`font-mono text-[11px] uppercase tracking-[0.08em] rounded-md border px-3 py-1.5 transition-all ${
                      isActive
                        ? "border-champ-light bg-champ-light text-aubergine"
                        : "border-champ-light/15 text-bone/65 hover:text-bone hover:border-champ-light/30"
                    }`}
                  >
                    {c} <span className={`ml-1 ${isActive ? "text-aubergine/60" : "text-bone/45"}`}>{countsByCat[c] || 0}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center text-bone/55"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : members.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-champ-light/15 p-16 text-center" data-testid="ecosystem-members-empty">
              <p className="font-serif text-2xl text-bone">No members yet</p>
              <p className="mt-2 text-sm text-bone/55">The directory will populate as admins add members through the console.</p>
              <Link to="/contact?inquiry=partner" className="mt-6 inline-flex items-center text-champagne hover:text-champ-light font-mono text-[11px] uppercase tracking-[0.15em]">
                Apply to join<ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Link>
            </div>
          ) : directory.length === 0 ? (
            <div className="rounded-lg border border-champ-light/10 bg-aubergine-tier p-10 text-center" data-testid="ecosystem-no-results">
              <p className="text-sm text-bone/65">No members match your filters.</p>
              <button onClick={() => { setActiveCat(null); setQ(""); }} className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-champagne hover:text-champ-light">
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="ecosystem-directory-grid">
                {pagedDirectory.map((m, i) => (
                  <Reveal key={m.member_id} delay={(i % 9) * 60}>
                    <MemberCard m={m} />
                  </Reveal>
                ))}
              </div>
              {totalPages > 1 && (
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={(p) => {
                    setPage(p);
                    // Bring the top of the directory back into view so the user
                    // isn't scrolled deep into the previous page's tail.
                    const el = document.getElementById("directory");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                />
              )}
            </>
          )}
        </div>
      </section>

      <ClosingCTA variant="contact" tone="night" />
      <Footer />
    </main>
  );
}
